import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { Star, Home, Users, Lock, Wrench, Droplet, Paintbrush } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';

// Category data mapping
const CATEGORY_DATA: Record<string, any> = {
  cleaning: {
    title: 'End of Tenancy',
    subtitle: 'Cleaning Excellence',
    label: 'PREMIUM ESTATE MANAGEMENT',
    description: 'Ensure a seamless transition with our meticulous cleaning services designed for premium properties. Guaranteed deposit returns and landlord satisfaction.',
    stats: [
      { value: '100%', label: 'DEPOSIT SUCCESS' },
      { value: '24h', label: 'FAST TURNAROUND' },
      { value: '5★', label: 'EXPERT RATING' }
    ],
  },
  plumbing: {
    title: 'Professional',
    subtitle: 'Plumbing Solutions',
    label: 'EXPERT WATER MANAGEMENT',
    description: 'Expert drainage, heating, and water management solutions for residential and commercial properties. Installations delivered by certified professionals.',
    stats: [
      { value: '98%', label: 'COMPLETION RATE' },
      { value: '4h', label: 'EMERGENCY RESPONSE' },
      { value: '4.9★', label: 'EXPERT RATING' }
    ],
  },
  maintenance: {
    title: 'Preventative',
    subtitle: 'Maintenance Solutions',
    label: 'STRUCTURAL PRESERVATION',
    description: 'Preserving the integrity of your property through preventative care and timely structural repairs. Regular maintenance ensures long-term property value.',
    stats: [
      { value: '95%', label: 'SATISFACTION RATE' },
      { value: '48h', label: 'SCHEDULING' },
      { value: '5★', label: 'EXPERT RATING' }
    ],
  },
  handyman: {
    title: 'Versatile',
    subtitle: 'Handyman Services',
    label: 'PROFESSIONAL INSTALLATIONS',
    description: 'Versatile trades and assembly work for any home improvement. We handle varied project needs with precision and care.',
    stats: [
      { value: '100%', label: 'PROJECT SUCCESS' },
      { value: '72h', label: 'TYPICAL TURNAROUND' },
      { value: '5★', label: 'EXPERT RATING' }
    ],
  },
  decorating: {
    title: 'Creative',
    subtitle: 'Decorating Services',
    label: 'AESTHETIC TRANSFORMATION',
    description: 'Revitalizing your aesthetic space with professional painting, wallpapering, and artisan finishes. Expert craftsmanship for every project.',
    stats: [
      { value: '100%', label: 'QUALITY STANDARD' },
      { value: '5-10 days', label: 'TYPICAL TIMELINE' },
      { value: '5★', label: 'EXPERT RATING' }
    ],
  },
  logistics: {
    title: 'Efficient',
    subtitle: 'Logistics & Waste',
    label: 'RESPONSIBLE MANAGEMENT',
    description: 'Responsible disposal and organized removals, handled with care for both your goods and the environment. Professional logistics coordination.',
    stats: [
      { value: '1000+', label: 'MOVES COMPLETED' },
      { value: '24h', label: 'SCHEDULING' },
      { value: '4.8★', label: 'EXPERT RATING' }
    ],
  },
};

const defaultCategories = [
  {
    icon: Home,
    title: 'For Landlords',
    description: 'Professional property management and coordination designed for landlord portfolios. We handle the details so you can focus on your investment.'
  },
  {
    icon: Users,
    title: 'For Letting Agents',
    description: 'Efficient scheduling and professional service delivery for your client properties. We provide documentation and comprehensive compliance support.'
  },
  {
    icon: Lock,
    title: 'For Tenants',
    description: 'Help secure your deposit return with our expert services. Leave your property in perfect condition. Hassle-free handover process.'
  }
];

const ServiceCategoryPage = () => {
  const { category } = useParams<{ category: string }>();
  
  // Get category data or use default
  const serviceData = CATEGORY_DATA[category || 'cleaning'] || CATEGORY_DATA.cleaning;

  return (
    <Layout>
      <Helmet>
        <title>{serviceData.title} | Virewire - Professional Property Services</title>
        <meta name="description" content={serviceData.description} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: Content */}
            <div>
              <p className="text-xs font-semibold text-teal-600 tracking-widest mb-6">
                {serviceData.label}
              </p>
              <h1 className="text-5xl sm:text-6xl font-serif font-light mb-2">
                {serviceData.title}
              </h1>
              <p className="text-4xl sm:text-5xl font-serif italic text-gray-700 mb-6">
                {serviceData.subtitle}
              </p>
              
              <p className="text-gray-600 text-lg leading-relaxed mb-8 max-w-lg">
                {serviceData.description}
              </p>

              {/* Stats */}
              <div className="flex gap-8 mb-8">
                {serviceData.stats.map((stat, idx) => (
                  <div key={idx}>
                    <div className="text-2xl sm:text-3xl font-bold text-teal-600 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Form */}
            <div className="bg-gray-50 rounded-lg p-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">Request a Bespoke Quote</h3>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                    Property Type
                  </label>
                  <select className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600">
                    <option>Studio / 1-bedroom Apartment</option>
                    <option>2-bedroom home</option>
                    <option>3+ bedroom home</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                    Preferred Date
                  </label>
                  <input type="date" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase mb-2">
                    Postcode
                  </label>
                  <input type="text" placeholder="+44 000 000 000" className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" />
                </div>

                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 mt-6">
                  INITIALIZE BOOKING
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Who Uses This Service */}
      <section className="bg-gray-50 py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-serif text-center mb-16">Who Uses This Service</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {defaultCategories.slice(0, 2).map((cat, idx) => {
              const Icon = cat.icon;
              return (
                <div key={idx} className="bg-white rounded-lg p-8 shadow-sm">
                  <Icon className="h-8 w-8 text-teal-600 mb-6" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{cat.title}</h3>
                  <p className="text-gray-600 text-sm mb-6">{cat.description}</p>
                  <Link to="#" className="text-xs font-semibold text-teal-600 hover:text-teal-700 uppercase tracking-wide">
                    Learn More
                  </Link>
                </div>
              );
            })}

            {/* Highlighted Card */}
            {(() => {
              const ThirdIcon = defaultCategories[2].icon;
              return (
                <div className="bg-teal-600 text-white rounded-lg p-8">
                  <ThirdIcon className="h-8 w-8 text-white mb-6" />
                  <h3 className="text-xl font-semibold mb-4">{defaultCategories[2].title}</h3>
                  <p className="text-teal-50 text-sm mb-6">{defaultCategories[2].description}</p>
                  <Link to="#" className="text-xs font-semibold text-white hover:text-teal-100 uppercase tracking-wide">
                    Secure Deposit
                  </Link>
                </div>
              );
            })()} 
          </div>
        </div>
      </section>

      {/* Assurance Policy */}
      <section className="bg-teal-700 text-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <h2 className="text-5xl font-serif font-light mb-8">
                The Curator's<br />
                <span className="italic">Assurance Policy</span>
              </h2>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <Lock className="h-6 w-6 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">LIABILITY PROTECTION</h4>
                    <p className="text-teal-50 text-sm">
                      Comprehensive coverage for high-value and structural elements and bespoke homes.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Lock className="h-6 w-6 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">AUDIT-READY DOCUMENTATION</h4>
                    <p className="text-teal-50 text-sm">
                      Every project includes a digital ledger of tasks completed, certified by our site specialists.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <Lock className="h-6 w-6 shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold mb-1">VETTED PROFESSIONAL NETWORK</h4>
                    <p className="text-teal-50 text-sm">
                      Only the top tier of service providers pass our rigorous quality and security checks.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Image */}
            <div className="relative h-80 lg:h-96 bg-teal-600 rounded-lg overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=600&fit=crop"
                alt="Professional service"
                className="w-full h-full object-cover opacity-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Ready to Delegate CTA */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <Star className="h-8 w-8 text-teal-600 mx-auto mb-6" />
          <h2 className="text-4xl sm:text-5xl font-serif font-light mb-6">
            Ready to Delegate?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Join the elite circle of homeowners and agents who trust Virewire for their most critical property transitions.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-3">
              START YOUR REQUEST
            </Button>
            <Button variant="outline" className="border-teal-600 text-teal-600 hover:bg-teal-50 font-semibold px-8 py-3">
              TALK TO A CURATOR
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServiceCategoryPage;
